import { useAtom } from "jotai";
import { useUpdateAtom } from "jotai/utils";
import dynamic from "next/dynamic";
import { useEffect, useRef } from "react";
import { useSendAnalyticsEvent } from "../analytics/events";
import useAnalytics from "../analytics/useAnalytics";
import { ContextMenuList } from "../av/ContextMenuList";
import CursorList from "./cursor/CursorList";
import PreviewList from "../av/PreviewList";
import ViewportList from "../av/ViewportList";
import BlockList from "../blocks/BlockList";
import { useTextPaste } from "../blocks/TextBlock";
import { DEFAULT_GRID_RESOLUTION, DEFAULT_SPACE_SIZE } from "../consts";
import { canvasRefAtom, mouseRefAtom, sideNavOnAtom } from "../jotai/atoms";
import { useBlocks, useBlocksRefSetup } from "../liveblocks/maps";
import { useSync } from "../liveblocks/useSync";
import { styled } from "../stitches.config";
import { Point2D } from "../types";
import Console from "../ui/console/Console";
import { useSetupCanvasPos } from "../utils/canvasPos";
import { handleDroppedImage, handlePaste } from "../utils/fileUpload";
import { gridToPixelSize } from "../utils/gridPosition";
import { useConnectionStatus } from "../utils/useConnectionStatus";
import { useFpsMeter } from "../utils/useFpsMeter";
import useSpaceScroll from "../utils/useSpaceScroll";
import { useWindowSize } from "../utils/useWindowSize";
import Chat from "./Chat";
import CursorIndicators from "./CursorIndicators";
import Doorbell from "./Doorbell";
import DrawMode from "./DrawMode";
import { useEdgeScroll } from "./EdgeScroll";
import { handleDroppedBlockData } from "./ExportJSON";
import Minimap from "./Minimap";
import { useSelectionKeyAction } from "./selection";
import { SpaceContextMenu } from "./SpaceContextMenu";
import { StartTemplate } from "./template/StartTemplate";
import useSpaceSessionTracker from "./tracking/useSpaceSessionTracker";
import { BlockContextMenu } from "../blocks/BlockContextMenu";

const AgoraStat = dynamic(() => import("./console/AgoraStat"), {
  ssr: false,
});
const MutedAudioWarning = dynamic(() => import("./MutedAudioWarning"), {
  ssr: false,
});

export const DEFAULT_SPACE_SIZE_PIXELS = gridToPixelSize(DEFAULT_SPACE_SIZE);

const DW = DEFAULT_SPACE_SIZE_PIXELS.width;
const DH = DEFAULT_SPACE_SIZE_PIXELS.height;

export default function Space() {
  useSpaceSessionTracker();
  useConnectionStatus();
  useFpsMeter();
  useAnalytics();
  useSync();
  useBlocksRefSetup();

  const setCanvasRefAtom = useUpdateAtom(canvasRefAtom);
  const windowSize = useWindowSize();
  const canvasRef = useRef<HTMLDivElement>(null);
  const canvasWrapperRef = useRef<HTMLDivElement>(null);
  const mousePositionRef = useRef<Point2D>({ x: 0, y: 0 });
  const setMouseRefAtom = useUpdateAtom(mouseRefAtom);
  const [sideNavOn] = useAtom(sideNavOnAtom);
  const sendAnalyticsEvent = useSendAnalyticsEvent();

  useSetupCanvasPos();

  useEffect(() => {
    setMouseRefAtom(mousePositionRef);
  }, [mousePositionRef, setMouseRefAtom]);

  useSpaceScroll();
  useEdgeScroll();
  useSelectionKeyAction();

  const { upsertBlock } = useBlocks();
  const handleTextPaste = useTextPaste();

  // Pasting images
  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      handlePaste(
        e.clipboardData,
        upsertBlock,
        mousePositionRef.current,
        sendAnalyticsEvent
      );

      if (
        document.activeElement?.nodeName !== "TEXTAREA" &&
        document.activeElement?.nodeName !== "INPUT"
      )
        handleTextPaste(e.clipboardData, upsertBlock, mousePositionRef.current);
    }

    function onDrop(e: DragEvent) {
      const items = e.dataTransfer?.items;
      if (items) {
        // handle dropped JSON block data
        handleDroppedBlockData(upsertBlock, e, items);
      }

      // handle dragged image from imageSearch
      let imageHandled = false;
      try {
        const data = JSON.parse(e!.dataTransfer!.getData("text"));
        const { url, width, height } = data;
        if (url && width & height) {
          handleDroppedImage(
            width,
            height,
            mousePositionRef,
            url,
            upsertBlock,
            sendAnalyticsEvent
          );
          // Prevent the image handled again by below handlePaste() due to
          // modern browsers enabling image drag and drop by default.
          imageHandled = true;
        }
      } catch (e) {
        // unknown text drop
      }
      //handle dropped image
      if (!imageHandled)
        handlePaste(e.dataTransfer, upsertBlock, mousePositionRef.current);
      e.preventDefault(); // prevent opening file when dragging file in
    }

    function onDragOver(e: DragEvent) {
      e.preventDefault(); // prevent opening file when dragging file in
    }

    document.addEventListener("paste", onPaste);
    window.addEventListener("drop", onDrop);
    window.addEventListener("dragover", onDragOver);
    return () => {
      document.removeEventListener("paste", onPaste);
      window.removeEventListener("drop", onDrop);
      window.removeEventListener("dragover", onDragOver);
    };
  }, [upsertBlock, handleTextPaste]);

  // Get canvasRef to use instead of threading everywhere
  useEffect(() => {
    if (canvasRef?.current) {
      setCanvasRefAtom(canvasRef!.current!);
    }
  }, [canvasRef, setCanvasRefAtom]);

  const classForScreenshotBot = "space-loaded";
  return (
    <>
      <CanvasWrapper
        ref={canvasWrapperRef}
        id="#main"
        className={classForScreenshotBot}
      >
        <CanvasBackground />
        <Canvas
          data-cursor={"default"}
          className="main-canvas"
          ref={canvasRef}
          leftShift={sideNavOn}
        >
          <BlockContextMenu />
          <SpaceContextMenu addBlock={upsertBlock}></SpaceContextMenu>
          <CanvasGrid className="background" />
          <ViewportList />
          <ContextMenuList />
          <PreviewList />
          <BlockList />
          <CursorList upsertBlock={upsertBlock} />
          <StartTemplate />
        </Canvas>
      </CanvasWrapper>
      <CursorIndicators />
      <DrawMode />
      <Console />
      <AgoraStat />
      <Chat />
      <Tools>
        <Doorbell />
        <MutedAudioWarning />
        <Minimap windowSize={windowSize} />
      </Tools>
    </>
  );
}
const Canvas = styled("main", {
  position: "relative",
  userSelect: "none",
  width: DW,
  height: DH,
  // cursor: "none",
  transition: "transform 100ms ease-in-out",
  variants: {
    leftShift: {
      true: {
        transform: "translateX($sizes$sideNavWidth)",
      },
    },
  },
});

const CanvasBackground = styled("div", {
  position: "absolute",
  top: 0,
  bottom: 0,
  right: 0,
  left: 0,
  backgroundColor: "$paper95",
});

export const CanvasGrid = styled("div", {
  position: "absolute",
  top: 0,
  bottom: 0,
  right: 0,
  left: 0,
  backgroundImage: `linear-gradient(to right, $paper90 1px, transparent 1px),
                    linear-gradient(to bottom, $paper90 1px, transparent 1px)`,
  backgroundSize: `${DEFAULT_GRID_RESOLUTION}px ${DEFAULT_GRID_RESOLUTION}px`,
  opacity: 0.95,
  pointerEvents: "none",
  transition: "opacity 2.5s linear", // handle removing of keyframe fade ?
});

const CanvasWrapper = styled("div", {
  width: DW,
  height: DH,
  transformOrigin: "50% 50%",
});

const Tools = styled("div", {
  position: "fixed",
  bottom: "$2",
  right: "$2",
  display: "grid",
  gridAutoFlow: "column",
  gridGap: "$1",
  alignItems: "end",
  zIndex: "$sideNav",
});
